import { isValidPhoneNumber } from 'libphonenumber-js';
import { isValidEmail } from '../../../utils/emailValidation';
import { buildApiSegmentFiltersPayload, parseTagsInput } from './contactsApiMapper';

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 100;
const MAX_LEAD_STATUS_LENGTH = 50;
const MAX_TAG_LENGTH = 40;
const MAX_TAGS = 20;

export function validateContactName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return 'Name is required';
  if (trimmed.length < MIN_NAME_LENGTH) {
    return `Name must be at least ${MIN_NAME_LENGTH} characters`;
  }
  if (trimmed.length > MAX_NAME_LENGTH) {
    return `Name must not exceed ${MAX_NAME_LENGTH} characters`;
  }
  return '';
}

export function validateContactPhone(phone) {
  const trimmed = (phone || '').trim();
  if (!trimmed) return 'Phone is required';
  if (!isValidPhoneNumber(trimmed)) {
    return 'Enter a valid phone number with country code (e.g. +91 98765 43210)';
  }
  return '';
}

export function validateContactEmail(email) {
  const trimmed = (email || '').trim();
  if (!trimmed) return '';
  if (!isValidEmail(trimmed)) return 'Enter a valid email address';
  return '';
}

export function validateContactLeadStatus(leadStatus) {
  const trimmed = String(leadStatus || '').trim();
  if (!trimmed) return '';
  if (trimmed.length > MAX_LEAD_STATUS_LENGTH) {
    return `Lead status must not exceed ${MAX_LEAD_STATUS_LENGTH} characters`;
  }
  return '';
}

export function validateContactTags(tagsInput) {
  const tags = parseTagsInput(tagsInput);
  if (!tags.length) return '';

  if (tags.length > MAX_TAGS) {
    return `Add at most ${MAX_TAGS} tags`;
  }

  const tooLong = tags.find((tag) => tag.length > MAX_TAG_LENGTH);
  if (tooLong) {
    return `Each tag must be at most ${MAX_TAG_LENGTH} characters`;
  }

  return '';
}

export function validateAddContactForm(form) {
  return {
    name: validateContactName(form.name),
    phone: validateContactPhone(form.phone),
    email: validateContactEmail(form.email),
    leadStatus: validateContactLeadStatus(form.leadStatus),
    tags: validateContactTags(form.tags),
  };
}

export function validateUpdateContactForm({ email }) {
  return {
    email: validateContactEmail(email),
  };
}

export function hasContactValidationErrors(errors) {
  return Object.values(errors).some((message) => message);
}

const SUPPORTED_SEGMENT_FILTER_FIELDS = ['lead_status', 'tags'];
const MIN_SEGMENT_NAME_LENGTH = 2;
const MAX_SEGMENT_NAME_LENGTH = 100;

export function validateSegmentName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return 'Segment name is required';
  if (trimmed.length < MIN_SEGMENT_NAME_LENGTH) {
    return `Segment name must be at least ${MIN_SEGMENT_NAME_LENGTH} characters`;
  }
  if (trimmed.length > MAX_SEGMENT_NAME_LENGTH) {
    return `Segment name must not exceed ${MAX_SEGMENT_NAME_LENGTH} characters`;
  }
  return '';
}

export function validateSegmentSave({ name, uiFilters }) {
  const errors = {};
  const nameError = validateSegmentName(name);
  if (nameError) {
    errors.name = nameError;
  }

  const activeFilters = (uiFilters || []).filter(
    (filter) =>
      filter.value ||
      (filter.field === 'custom_attribute' && filter.attributeKey)
  );

  if (!activeFilters.length) {
    errors.filters = 'Add at least one filter before saving a segment';
    return errors;
  }

  const unsupported = activeFilters.filter(
    (filter) => !SUPPORTED_SEGMENT_FILTER_FIELDS.includes(filter.field)
  );
  const supportedActive = activeFilters.filter((filter) =>
    SUPPORTED_SEGMENT_FILTER_FIELDS.includes(filter.field)
  );

  if (!supportedActive.length) {
    errors.filters = 'Segments only support Lead Status and Tags filters';
    return errors;
  }

  if (unsupported.length) {
    errors.filters =
      'Remove unsupported filters. Segments only save Lead Status and Tags.';
    return errors;
  }

  const apiFilters = buildApiSegmentFiltersPayload(uiFilters);
  if (!apiFilters.leadStatus && !apiFilters.tags?.length) {
    errors.filters = 'Add at least one Lead Status or Tags filter with a value';
  }

  return errors;
}

export function validateImportContactsMapping(mapping) {
  if (!mapping?.phone?.trim()) {
    return 'Could not detect a phone column. Use a header such as "Phone", "Mobile", or "WhatsApp".';
  }
  return '';
}

export function validateImportContactsRows(rows, mapping) {
  const mappingError = validateImportContactsMapping(mapping);
  if (mappingError) return mappingError;

  if (!rows?.length) {
    return 'The CSV has no contact data rows to import.';
  }

  const phoneHeader = mapping.phone;
  const rowsWithPhone = rows.filter((row) =>
    String(row?.[phoneHeader] || '').trim()
  );

  if (!rowsWithPhone.length) {
    return `No phone numbers found under the "${phoneHeader}" column.`;
  }

  return '';
}
